import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  apaNameInitialsFirst,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaInTextAuthors,
  naturalName,
  type PersonName,
  parsePartialDate,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mainTitle,
  mlaUrl,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  andList,
  apaAmpersandList,
  chicagoShortTitle,
  type MediaLabels,
  type MediaValues,
  mlaRoleNames,
  mlaShortTitle,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";

/*
 * Audiobooks in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7, Publication Manual §10.2, the audiobook example — "Cain, S. (2012). Quiet:
 *   The power of introverts in a world that can't stop talking (K. Mazur, Narr.)
 *   [Audiobook]. Random House Audio." — and pp. 321–322: note the audiobook format
 *   only when it matters (abridged content, the narration, quoting with a time stamp);
 *   otherwise cite the book (as reproduced by Royal Roads University's WriteAnswers).
 *   "Narr." / "Narrs." from the Manual's abbreviation table (pp. 306–307). An
 *   audiobook released after the print book is a republished work: "(Original work
 *   published 1960)" and "(Lee, 1960/2014)" — APA Style, "Book/ebook references" (the
 *   Watson & Rayner example) and "Children's book references" (illustrator and edition
 *   share one parenthesis, separated by a semicolon). Quotations take a time stamp in
 *   place of a page: "(Cuddy, 2012, 2:12)" — APA Style, "Direct quotation of material
 *   without page numbers".
 * - MLA 9: the Handbook's audiobook examples, e.g. "Lee, Harper. To Kill a
 *   Mockingbird. Narrated by Sissy Spacek, audiobook ed., unabridged ed., Caedmon,
 *   2014." (reproduced in Phillips Academy's MLA 9 guide). MLA Style Center, "How do I
 *   cite an audiobook?": a website you listened on is a second container with its URL
 *   (an app you downloaded from is not cited), and an in-text citation gives the time
 *   stamp — "(Atwood 01:47:12-29)".
 * - Chicago 18 §14.58, "Downloadable ebook formats and audiobooks": cited like a book,
 *   with the reader ("read by"), the format and the running time; a passage is located
 *   by its time — "at 3 hr., 14 min." or the player's "03:14:00" (Chicago Style Q&A on
 *   time stamps). §14.30: no place of publication.
 */

export const AUDIOBOOK_FIELDS = {
  authors: "authors",
  title: "title",
  narrators: "narrators",
  publisher: "publisher",
  year: "year",
  originalYear: "originalYear",
  edition: "edition",
  duration: "duration",
  platform: "platform",
  url: "url",
  timestamp: "timestamp",
} as const;

const F = AUDIOBOOK_FIELDS;

export type AudiobookEdition = "" | "unabridged" | "abridged";

export interface AudiobookLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Audiobook {
  authors: PersonName[];
  title: string;
  narrators: PersonName[];
  publisher: string;
  year: string;
  originalYear: string;
  edition: AudiobookEdition;
  duration: string;
  platform: string;
  url: string;
  timestamp: string;
}

const RE_YEAR = /^\d{4}$/;

function readAudiobook(values: MediaValues): Audiobook {
  const date = parsePartialDate(get(values, F.year));
  const original = get(values, F.originalYear);
  const edition = get(values, F.edition);
  return {
    authors: parsePeople(get(values, F.authors)),
    title: get(values, F.title),
    narrators: parsePeople(get(values, F.narrators)),
    publisher: get(values, F.publisher),
    year: date ? String(date.year) : "",
    originalYear: RE_YEAR.test(original) ? original : "",
    edition: edition === "unabridged" || edition === "abridged" ? edition : "",
    duration: runningTime(get(values, F.duration)),
    platform: get(values, F.platform),
    url: get(values, F.url),
    timestamp: get(values, F.timestamp),
  };
}

/* ------------------------------------------------------------ time and length */

const RE_CLOCK = /^(\d+):([0-5]?\d)(?::([0-5]?\d))?$/;
const RE_HOURS = /(\d+)\s*(?:h|hr|hrs|hour|hours)\b\.?/i;
const RE_MINUTES = /(\d+)\s*(?:m|min|mins|minute|minutes)\b\.?/i;
const RE_SECONDS = /(\d+)\s*(?:s|sec|secs|second|seconds)\b\.?/i;

interface Span {
  hours: number;
  minutes: number;
  seconds: number;
}

function readSpan(text: string): Span | null {
  const clock = RE_CLOCK.exec(text);
  if (clock) {
    return {
      hours: Number(clock[1]),
      minutes: Number(clock[2]),
      seconds: clock[3] ? Number(clock[3]) : 0,
    };
  }
  const hours = RE_HOURS.exec(text);
  const minutes = RE_MINUTES.exec(text);
  const seconds = RE_SECONDS.exec(text);
  if (!(hours || minutes || seconds)) {
    return null;
  }
  return {
    hours: hours ? Number(hours[1]) : 0,
    minutes: minutes ? Number(minutes[1]) : 0,
    seconds: seconds ? Number(seconds[1]) : 0,
  };
}

/**
 * Chicago's running time: "12 hr., 17 min." from "12:17", "12h 17m" or "12 hr 17
 * min". Anything it cannot read is returned as typed.
 */
export function runningTime(text: string): string {
  const trimmed = text.trim();
  if (trimmed === "") {
    return "";
  }
  const span = readSpan(trimmed);
  if (!span) {
    return trimmed;
  }
  const pieces: string[] = [];
  if (span.hours > 0) {
    pieces.push(`${span.hours} hr.`);
  }
  if (span.minutes > 0) {
    pieces.push(`${span.minutes} min.`);
  }
  if (span.seconds > 0) {
    pieces.push(`${span.seconds} sec.`);
  }
  return pieces.length > 0 ? pieces.join(", ") : trimmed;
}

const RE_TIME_STAMP = /^(?:(\d+):)?([0-5]?\d):([0-5]\d)$/;

/** MLA's time stamp, padded the way a player shows it: "1:25:40" → "01:25:40". */
export function mlaTimeStamp(text: string): string {
  const match = RE_TIME_STAMP.exec(text.trim());
  if (!match) {
    return text.trim();
  }
  const pad = (value: string) => value.padStart(2, "0");
  return `${pad(match[1] ?? "0")}:${pad(match[2])}:${match[3]}`;
}

/* ---------------------------------------------------------------------- APA */

function apaYear(book: Audiobook): string {
  const year = book.year || "n.d.";
  if (book.originalYear && book.originalYear !== book.year && book.year) {
    return `${book.originalYear}/${year}`;
  }
  return year;
}

/** "(S. Spacek, Narr.)", "(C. Booth & A. Ojo, Narrs.; Abridged ed.)" or "". */
function apaNarration(book: Audiobook): string {
  const pieces: string[] = [];
  if (book.narrators.length > 0) {
    const names = apaAmpersandList(book.narrators.map(apaNameInitialsFirst));
    pieces.push(`${names}, ${book.narrators.length > 1 ? "Narrs." : "Narr."}`);
  }
  if (book.edition === "abridged") {
    pieces.push("Abridged ed.");
  }
  return pieces.length > 0 ? ` (${pieces.join("; ")})` : "";
}

function apaReference(book: Audiobook): CitationParts {
  const title = capitalizeSubtitle(book.title);
  const date = `(${book.year || "n.d."}).`;
  const tail: string[] = [];
  if (book.publisher) {
    tail.push(`${book.publisher}${closer(book.publisher)}`);
  }
  if (book.url) {
    tail.push(book.url);
  }
  const reissue =
    book.originalYear && book.originalYear !== book.year
      ? ` (Original work published ${book.originalYear})`
      : "";
  const after = `${apaNarration(book)} [Audiobook].`;
  const end = `${tail.length > 0 ? ` ${tail.join(" ")}` : ""}${reissue}`;
  if (book.authors.length === 0) {
    return tidyParts([italic(title), plain(`${after} ${date}${end}`)]);
  }
  const authors = apaAuthors(book.authors);
  return tidyParts([
    plain(`${authors}${closer(authors)} ${date} `),
    italic(title),
    plain(`${after}${end}`),
  ]);
}

function apaOutput(book: Audiobook, labels: AudiobookLabels): CitationOutput {
  const year = apaYear(book);
  const stamp = book.timestamp ? `, ${book.timestamp}` : "";
  const shortTitle = mainTitle(titleCase(book.title));
  const parenthetical: CitationParts =
    book.authors.length > 0
      ? [plain(`(${apaInTextAuthors(book.authors)}, ${year}${stamp})`)]
      : [plain("("), italic(shortTitle), plain(`, ${year}${stamp})`)];
  const narrative: CitationParts =
    book.authors.length > 0
      ? [plain(`${apaInTextAuthors(book.authors, true)} (${year})`)]
      : [italic(shortTitle), plain(` (${year})`)];
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(book) },
      { label: labels.parenthetical, parts: parenthetical },
      { label: labels.narrative, parts: narrative },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function mlaOutput(book: Audiobook, labels: AudiobookLabels): CitationOutput {
  const title = titleCase(capitalizeSubtitle(book.title));
  const container: string[] = [];
  if (book.narrators.length > 0) {
    container.push(
      `narrated by ${mlaRoleNames(book.narrators.map(naturalName))}`
    );
  }
  container.push("audiobook ed.");
  if (book.edition) {
    container.push(`${book.edition} ed.`);
  }
  if (book.publisher) {
    container.push(book.publisher);
  }
  if (book.year) {
    container.push(book.year);
  }
  if (book.url && !book.platform) {
    container.push(mlaUrl(book.url));
  }
  const parts: CitationParts = [];
  if (book.authors.length > 0) {
    const authors = mlaAuthors(book.authors);
    parts.push(plain(`${authors}${closer(authors)} `));
  }
  parts.push(italic(title), plain(`${closer(title)} `));
  parts.push(plain(`${capitalizeFirst(container.join(", "))}.`));
  if (book.url && book.platform) {
    parts.push(
      plain(" "),
      italic(book.platform),
      plain(`, ${mlaUrl(book.url)}.`)
    );
  }
  const stamp = book.timestamp ? ` ${mlaTimeStamp(book.timestamp)}` : "";
  const inText: CitationParts =
    book.authors.length > 0
      ? [plain(`(${mlaInTextAuthors(book.authors)}${stamp})`)]
      : [plain("("), italic(mlaShortTitle(title)), plain(`${stamp})`)];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(parts) },
      { label: labels.inText, parts: inText },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoMedium(book: Audiobook): string {
  const medium = book.platform ? `${book.platform} audiobook` : "audiobook";
  return book.edition === "abridged" ? `${medium}, abridged` : medium;
}

function chicagoNote(book: Audiobook, title: string): CitationParts {
  const readers = book.narrators.map(naturalName);
  const facts = [book.publisher, book.year || "n.d."].filter(Boolean);
  const tail = [
    `(${facts.join(", ")})`,
    chicagoMedium(book),
    book.timestamp ? `at ${book.timestamp}` : book.duration,
    book.url,
  ].filter(Boolean);
  const parts: CitationParts = [];
  if (book.authors.length > 0) {
    parts.push(plain(`${chicagoNoteAuthors(book.authors)}, `));
  }
  parts.push(italic(title));
  const readBy = readers.length > 0 ? `, read by ${andList(readers)}` : "";
  parts.push(plain(`${readBy} ${withPeriod(tail.join(", "))}`));
  return tidyParts(parts);
}

function chicagoShortNote(book: Audiobook, title: string): CitationParts {
  const stamp = book.timestamp ? `, at ${book.timestamp}` : "";
  const short = chicagoShortTitle(title);
  if (book.authors.length === 0) {
    return [italic(short), plain(`${stamp}.`)];
  }
  return [
    plain(`${chicagoShortAuthors(book.authors)}, `),
    italic(short),
    plain(`${stamp}.`),
  ];
}

function chicagoBibliography(book: Audiobook, title: string): CitationParts {
  const readers = book.narrators.map(naturalName);
  const parts: CitationParts = [];
  if (book.authors.length > 0) {
    const authors = chicagoBibliographyAuthors(book.authors);
    parts.push(plain(`${authors}${closer(authors)} `));
  }
  parts.push(italic(title), plain(`${closer(title)}`));
  if (readers.length > 0) {
    parts.push(plain(` Read by ${andList(readers)}.`));
  }
  const facts = [book.publisher, book.year || "n.d."].filter(Boolean);
  parts.push(plain(` ${facts.join(", ")}.`));
  const medium = capitalizeFirst(
    [chicagoMedium(book), book.duration].filter(Boolean).join(", ")
  );
  parts.push(plain(` ${withPeriod(medium)}`));
  if (book.url) {
    parts.push(plain(` ${book.url}.`));
  }
  return tidyParts(parts);
}

function chicagoOutput(
  book: Audiobook,
  labels: AudiobookLabels
): CitationOutput {
  const title = titleCase(capitalizeSubtitle(book.title));
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(book, title) },
      { label: labels.shortNote, parts: chicagoShortNote(book, title) },
      { label: labels.bibliography, parts: chicagoBibliography(book, title) },
    ],
    note: labels.chicagoNote,
  };
}

export function audiobookIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatAudiobook(
  values: MediaValues,
  labels: AudiobookLabels
): CitationOutput[] {
  const book = readAudiobook(values);
  return [
    apaOutput(book, labels),
    mlaOutput(book, labels),
    chicagoOutput(book, labels),
  ];
}
